/* eslint-disable promise/prefer-await-to-callbacks */
import { combineEpics, Epic, ofType, StateObservable } from 'redux-observable';
import { EMPTY, from, of } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import store, { IStoreState } from 'store';
import { addToQueueSuccess, sendForTranslationSuccess } from 'store/content/content.actions';
import { api } from 'utils/axiosInstance';
import { apiErrorHandler } from 'utils/handlers/notificationHandler';
import {
  clearActiveProjects,
  clearSettings,
  getActiveProjects,
  getActiveProjectsError,
  getActiveProjectsSuccess,
  getAvailableLanguages,
  getAvailableLanguagesError,
  getAvailableLanguagesSuccess,
  getCustomers,
  getCustomersError,
  getCustomersSuccess,
  getKenticoProjects,
  getKenticoProjectsError,
  getKenticoProjectsSuccess,
  getTemplatesByCustomerId,
  getTemplatesByCustomerIdError,
  getTemplatesByCustomerIdSuccess,
} from './settings.actions';

export const getCustomersEpic: Epic = (action$) =>
  action$.pipe(
    ofType(getCustomers.type),
    switchMap(() =>
      from(api.getXTMCustomers$GET$api_content_customers()).pipe(
        switchMap(({ data: xtmCustomer }) => {
          store.dispatch(getCustomersSuccess(xtmCustomer));

          if (xtmCustomer.defaultCustomerId) {
            return of(getTemplatesByCustomerId(xtmCustomer.defaultCustomerId));
          }
          return EMPTY;
        }),
        catchError((error) => {
          apiErrorHandler(error);
          return of(getCustomersError());
        }),
      ),
    ),
  );

export const getTemplatesByCustomerIdEpic: Epic = (action$) =>
  action$.pipe(
    ofType(getTemplatesByCustomerId.type),
    switchMap(({ payload: customerId }) =>
      from(api.getXTMTemplatesForExistingUser(customerId)).pipe(
        switchMap(({ data: xtmTemplate }) => {
          store.dispatch(getTemplatesByCustomerIdSuccess(xtmTemplate));

          if (
            xtmTemplate.defaultTemplateId &&
            xtmTemplate.templates.find((template) => template.xtmTemplateId === xtmTemplate.defaultTemplateId)
          ) {
            // @TODO
            return EMPTY;
          }

          return EMPTY;
        }),
        catchError((error) => {
          apiErrorHandler(error);
          return of(getTemplatesByCustomerIdError());
        }),
      ),
    ),
  );

export const getActiveProjectsEpic: Epic = (action$) =>
  action$.pipe(
    ofType(getActiveProjects.type, sendForTranslationSuccess.type, addToQueueSuccess.type),
    switchMap(({ payload }) =>
      from(api.getActiveXTMProjects(payload)).pipe(
        switchMap(({ data: activeProjects }) => of(getActiveProjectsSuccess(activeProjects))),
        catchError((error) => {
          apiErrorHandler(error);
          return of(getActiveProjectsError());
        }),
      ),
    ),
  );

export const getKenticoProjectsEpic: Epic = (action$) =>
  action$.pipe(
    ofType(getKenticoProjects.type),
    switchMap(() =>
      from(api.getAllActiveProjectsByUserId()).pipe(
        switchMap(({ data }) => of(getKenticoProjectsSuccess(data))),
        catchError((error) => {
          apiErrorHandler(error);

          return of(getKenticoProjectsError());
        }),
      ),
    ),
  );

export const clearSettingsEpic: Epic = (action$) =>
  action$.pipe(
    ofType(clearSettings.type),
    take(1),
    switchMap(() => of(clearSettings())),
  );

export const clearActiveProjectsEpic: Epic = (action$) =>
  action$.pipe(
    ofType(clearActiveProjects.type),
    take(1),
    switchMap(() => of(clearActiveProjects())),
  );

const getAvailableLanguagesEpic: Epic = (action$, store$: StateObservable<IStoreState>) =>
  action$.pipe(
    ofType(getAvailableLanguages.type),
    switchMap(() =>
      from(api.getProjectLanguageList(store$.value?.auth?.projectId || '')).pipe(
        switchMap(({ data }) => {
          return of(getAvailableLanguagesSuccess(data));
        }),
        catchError((error) => {
          apiErrorHandler(error);
          return of(getAvailableLanguagesError());
        }),
      ),
    ),
  );

export const settingsEpics = combineEpics(
  getCustomersEpic,
  getTemplatesByCustomerIdEpic,
  getActiveProjectsEpic,
  clearSettingsEpic,
  clearActiveProjectsEpic,
  getKenticoProjectsEpic,
  getAvailableLanguagesEpic,
);
